import React, {FC, useMemo, useState} from "react";
import {
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@material-ui/core";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import FfairModal from "view/components/modal/FfairModal";
import FfairPaper from "view/components/FfairPaper";
import Alert from "view/components/Alert";
import {
  ILibrarySectionMatch,
  IShowInfoPushResolution,
  ShowInfoPushAction,
} from "api/showInfoLibrary/types";
import {
  INTERNAL_NAME_MAX_LENGTH,
  TITLE_MAX_LENGTH,
} from "view/apps/Organiser/pages/Libraries/ShowInfo/constants";
import canSubmitPush, {collides, internalNameIssue} from "./canSubmitPush";
import defaultMatchId from "./defaultMatchId";
import useStyles from "./styles";

/**
 * The **push** side of `Duplicate warning`: the library already holds this
 * title. Either overwrite one of its variants — a dropdown, since matching
 * ignores the internal name — or keep both and name the new one, the only place
 * a Show ever asks for an internal name (Q14).
 */
type Props = {
  open: boolean;
  sectionTitle: string;
  matches: ILibrarySectionMatch[];
  onClose: () => void;
  onConfirm: (resolution: IShowInfoPushResolution) => Promise<void>;
};

/*
 * As the design lists them: `Section name 1 (A)`. The archived note stays —
 * replacing an archived master is a different act, and nothing else says so.
 */
const describe = (match: ILibrarySectionMatch): string => {
  const suffix = match.internalName ? ` (${match.internalName})` : "";
  const archived = match.archivedAt ? " — archived" : "";

  return `${match.title}${suffix}${archived}`;
};

const ShowInfoDuplicateWarningModal: FC<Props> = ({
  open,
  sectionTitle,
  matches,
  onClose,
  onConfirm,
}) => {
  const classes = useStyles();

  const [action, setAction] = useState<ShowInfoPushAction>(
    ShowInfoPushAction.REPLACE
  );
  const [masterTermId, setMasterTermId] = useState(defaultMatchId(matches));
  const [title, setTitle] = useState(sectionTitle);
  const [internalName, setInternalName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const nextTitle = title.trim();

  const stillColliding = useMemo(() => collides(nextTitle, matches), [
    matches,
    nextTitle,
  ]);

  /*
   * Every rule answers on its own field as it is typed, and the button reflects
   * the sum — being told only on submit meant guessing which name is free.
   */
  const internalNameError = useMemo(
    () => internalNameIssue(internalName, matches),
    [internalName, matches]
  );

  const internalNameRequired = stillColliding && !internalName.trim();

  const canSubmit = canSubmitPush({
    action,
    masterTermId,
    title,
    internalName,
    matches,
  });

  const submit = async () => {
    // Unreachable from the button; the guard is for any other caller.
    if (!canSubmit) return;

    setIsSubmitting(true);
    try {
      await onConfirm(
        action === ShowInfoPushAction.REPLACE
          ? {action, masterTermId}
          : {
              action,
              title: nextTitle,
              ...(internalName.trim() && {internalName: internalName.trim()}),
            }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FfairModal open={open} onClose={onClose} isCalledByAnotherModal>
      <FfairPaper className={classes.paper}>
        <Typography className={classes.header}>Duplicate warning</Typography>

        <Alert
          severity="warning"
          fullWidth
          className={classes.banner}
          customIcon={
            <ReportProblemOutlinedIcon className={classes.bannerIcon} />
          }
        >
          This Section name already exists in the Library, please select from
          the available options
        </Alert>

        <RadioGroup
          value={action}
          onChange={(event) =>
            setAction(event.target.value as ShowInfoPushAction)
          }
        >
          <Box className={classes.option}>
            <FormControlLabel
              value={ShowInfoPushAction.REPLACE}
              control={<Radio color="primary" />}
              classes={{label: classes.optionLabel}}
              label="Replace library section for local one"
            />
          </Box>

          {action === ShowInfoPushAction.REPLACE && (
            <Box className={classes.optionBody}>
              <Typography className={classes.optionHint}>
                Choose the library item that will be replaced
              </Typography>
              <TextField
                select
                fullWidth
                variant="outlined"
                label="Title *"
                className={classes.field}
                value={masterTermId}
                onChange={(event) => setMasterTermId(event.target.value)}
              >
                {matches.map((match) => (
                  <MenuItem key={match.id} value={match.id}>
                    {describe(match)}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}

          <Box className={classes.option}>
            <FormControlLabel
              value={ShowInfoPushAction.CREATE}
              control={<Radio color="primary" />}
              classes={{label: classes.optionLabel}}
              label="Create new section"
            />
          </Box>

          {action === ShowInfoPushAction.CREATE && (
            <Box className={classes.optionBody}>
              <Typography className={classes.optionHint}>
                Change section name or add an Internal name to continue
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                className={classes.field}
                label="Title *"
                value={title}
                error={!nextTitle}
                helperText={!nextTitle ? "A title is required." : undefined}
                onChange={(event) => setTitle(event.target.value)}
                inputProps={{maxLength: TITLE_MAX_LENGTH}}
              />
              <TextField
                fullWidth
                variant="outlined"
                className={classes.field}
                label="Internal name *"
                value={internalName}
                error={!!internalNameError}
                helperText={
                  internalNameError ??
                  (internalNameRequired
                    ? "Required while the title stays the same."
                    : undefined)
                }
                onChange={(event) => setInternalName(event.target.value)}
                inputProps={{maxLength: INTERNAL_NAME_MAX_LENGTH}}
              />
            </Box>
          )}
        </RadioGroup>

        <Box className={classes.actions}>
          <Button color="primary" disabled={isSubmitting} onClick={onClose}>
            Cancel
          </Button>
          {/* `Replace` or `Add new`, naming what the chosen option does. */}
          <Button
            color="primary"
            variant="contained"
            disabled={isSubmitting || !canSubmit}
            onClick={submit}
          >
            {action === ShowInfoPushAction.REPLACE ? "Replace" : "Add new"}
          </Button>
        </Box>
      </FfairPaper>
    </FfairModal>
  );
};

export default ShowInfoDuplicateWarningModal;
